import React, { useState, useEffect } from 'react';
import { BusinessIdea } from '../services/businessIdeaTypes';
import { BusinessPlan as BusinessPlanType, SectionId, SECTION_METADATA, BusinessPlanSection, GenerationContext } from '../types/businessPlan';
import { getBusinessPlanById, getLatestBusinessPlan, saveBusinessPlan, getSavedBusinessIdeas, getUserBusinessPlansFromSupabase, deleteBusinessPlanFromSupabase } from '../services/savedIdeasService';
import { AnthropicService } from '../services/anthropic/anthropicService';
import { deleteAllBusinessPlansForCurrentUser } from '../services/supabase/businessPlanService';
import SavedIdeasList from '../components/business-plan/SavedIdeasList';
import BusinessPlanDocument from '../components/business-plan/BusinessPlanDocument';
import SavedPlansNavigation from '../components/business-plan/SavedPlansNavigation';
import { GenerationProgress } from '../components/business-plan/GenerationProgress';
import BusinessPlanWizard, { BusinessPlanAdditionalData } from '../components/business-plan/BusinessPlanWizard';
import BusinessPlanPDFExport from '../components/business-plan/BusinessPlanPDFExport';
import { toast } from 'react-hot-toast';
import '../styles/BusinessPlan.css';

const anthropicService = new AnthropicService();

const createEmptySections = (): Record<SectionId, BusinessPlanSection> => {
  const sections: Record<SectionId, BusinessPlanSection> = {} as Record<SectionId, BusinessPlanSection>;
  
  Object.entries(SECTION_METADATA).forEach(([id, metadata]) => {
    sections[id as SectionId] = {
      id: id as SectionId,
      title: metadata.title,
      content: '',
      status: 'not-started',
      pageNumber: metadata.pageNumber || 0,
      subsections: [],
      error: undefined
    };
  });
  
  return sections;
};

const BusinessPlan: React.FC = () => {
  const [savedIdeas, setSavedIdeas] = useState<BusinessIdea[]>([]);
  const [savedPlans, setSavedPlans] = useState<BusinessPlanType[]>([]);
  const [selectedIdea, setSelectedIdea] = useState<BusinessIdea | null>(null);
  const [currentPlan, setCurrentPlan] = useState<BusinessPlanType | null>(null);
  
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState<boolean>(false);
  const [showPDF, setShowPDF] = useState<boolean>(false);
  const MAX_SAVED_PLANS = 5;

  // Load saved ideas and plans on component mount
  useEffect(() => {
    const loadSavedData = async () => {
      try {
        setIsLoading(true);
        
        // Load saved ideas
        const rawIdeas = await getSavedBusinessIdeas();
        // Ideas are already mapped in the getSavedBusinessIdeas function
        setSavedIdeas(rawIdeas);
        
        // Load saved plans - now gets the authenticated user automatically
        const plans = await getUserBusinessPlansFromSupabase();
        setSavedPlans(plans as BusinessPlanType[]);
        
        // Load latest plan if available
        const latestPlan = await getLatestBusinessPlan();
        if (latestPlan) {
          setCurrentPlan(latestPlan as BusinessPlanType);
          
          // If the plan has an associated idea, select it
          if (latestPlan.idea_id) {
            const associatedIdea = rawIdeas.find(idea => idea.id === latestPlan.idea_id);
            if (associatedIdea) {
              setSelectedIdea(associatedIdea);
            }
          }
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading saved data:', error);
        setError('Failed to load saved ideas and plans');
        toast.error('Failed to load saved ideas and plans');
        setIsLoading(false);
      }
    };
    
    loadSavedData();
  }, []);

  // Handle idea selection
  const handleIdeaSelect = async (idea: BusinessIdea) => {
    setSelectedIdea(idea);
    
    try {
      // Check if there's already a plan for this idea
      const existingPlan = await getBusinessPlanById(idea.id ?? '');
      
      if (existingPlan) {
        setCurrentPlan(existingPlan as BusinessPlanType);
        toast.success('Loaded existing business plan for this idea');
      } else {
        // Clear current plan if selecting a new idea without a plan
        setCurrentPlan(null);
      }
    } catch (error) {
      console.error('Error checking for existing plan:', error);
      setError('Failed to check for existing business plan');
    }
  };

  // Handle plan selection
  const handlePlanSelect = (plan: BusinessPlanType) => {
    setCurrentPlan(plan);
    
    // If the plan has an associated idea, select it
    if (plan.idea_id) {
      const associatedIdea = savedIdeas.find(idea => idea.id === plan.idea_id);
      if (associatedIdea) {
        setSelectedIdea(associatedIdea);
      }
    }
  };

  // Create a new plan
  const handleCreateNewPlan = () => {
    if (savedPlans.length >= MAX_SAVED_PLANS) {
      toast.error(`You've reached the maximum of ${MAX_SAVED_PLANS} saved plans. Please delete some plans to create new ones.`);
      return;
    }
    
    setShowWizard(true);
  };

  // Function to generate a business plan using Anthropic API
  const generateBusinessPlan = async (plan: BusinessPlanType, wizardData: BusinessPlanAdditionalData) => {
    if (isGenerating) {
      console.log('Generation is already in progress. Skipping...');
      return;
    }
    
    setIsGenerating(true);
    
    try {
      // Update the status to generating but DON'T save to Supabase
      const updatedPlan: BusinessPlanType = { ...plan, status: 'generating' };
      // Remove Supabase saving during generation
      // await saveBusinessPlan(updatedPlan);
      setCurrentPlan(updatedPlan);
      
      // Generate each section of the business plan
      const sectionIds = Object.keys(plan.sections || {}) as SectionId[];
      
      // Create a local copy of the plan to update during generation
      const workingPlan: BusinessPlanType = { ...updatedPlan };
      const updatedSections = { ...workingPlan.sections };
      
      for (const sectionId of sectionIds) {
        // Skip if the section is already generated
        if (plan.sections?.[sectionId].status === 'completed') continue;
        
        // Update section status to generating (in local memory only)
        updatedSections[sectionId].status = 'generating';
        // Don't save to Supabase, just update the UI
        workingPlan.sections = updatedSections;
        setCurrentPlan({ ...workingPlan });
        
        // Generate content for the section
        try {
          // Prepare context for generation
          const context: GenerationContext = {
            businessIdea: plan.businessIdea || { title: '', description: '' } as BusinessIdea,
            currentSection: sectionId,
            sections: updatedSections
          };
          
          if (wizardData) {
            context.additionalData = wizardData;
          }
          
          // Call Anthropic API to regenerate the section
          const regeneratedSection = await anthropicService.regenerateSection(sectionId, context);
          
          // Debug log both the API response and the processed content
          console.log(`Raw API Response for ${sectionId}:`, regeneratedSection);
          console.log(`Content length: ${regeneratedSection.content?.length || 0}`);
          console.log(`Content preview: ${regeneratedSection.content?.substring(0, 100)}...`);
          
          // Verify content exists before updating
          if (!regeneratedSection.content || regeneratedSection.content.trim() === '') {
            console.error(`No content returned for section ${sectionId}`);
            throw new Error(`Failed to generate content for ${sectionId}`);
          }
          
          // Update the section with generated content (in local memory only)
          updatedSections[sectionId] = regeneratedSection;
          workingPlan.sections = updatedSections;
          
          // Update UI but don't save to Supabase
          setCurrentPlan({ ...workingPlan });
          
        } catch (err) {
          console.error(`Error generating section ${sectionId}:`, err);
          // Mark section as failed (in local memory only)
          updatedSections[sectionId].status = 'error';
          updatedSections[sectionId].content = 'Failed to generate content. Please try again.';
          workingPlan.sections = updatedSections;
          setCurrentPlan({ ...workingPlan });
        }
      }
      
      // Now that ALL sections are generated, save the ENTIRE plan to Supabase once
      workingPlan.status = 'completed';
      
      // Log the complete plan before saving
      console.log('COMPLETE PLAN BEFORE SAVING:', JSON.stringify(workingPlan, null, 2));
      console.log('Sections with content:', Object.entries(workingPlan.sections)
        .filter(([_, section]) => section.content && section.content.length > 0)
        .map(([id, section]) => `${id}: ${section.content.length} chars`));
      
      // Finally save to Supabase once, with all content
      const savedCompletePlan = await saveBusinessPlan(workingPlan);
      console.log('PLAN AFTER SAVING TO SUPABASE:', savedCompletePlan);
      
      setCurrentPlan(savedCompletePlan);
      
      // Update the list of saved plans
      setSavedPlans(prev => {
        const existingPlanIndex = prev.findIndex(p => p.id === savedCompletePlan.id);
        if (existingPlanIndex >= 0) {
          const updatedPlans = [...prev];
          updatedPlans[existingPlanIndex] = savedCompletePlan;
          return updatedPlans;
        } else {
          return [savedCompletePlan, ...prev];
        }
      });
      
      toast.success('Business plan generation completed!');
      
    } catch (error) {
      console.error('Error in business plan generation:', error);
      setError('Failed to generate business plan. Please try again.');
      toast.error('Failed to generate business plan');
      
      // Update the plan status to indicate error
      const planWithError: BusinessPlanType = { ...plan, status: 'draft', uiError: 'Generation failed' };
      await saveBusinessPlan(planWithError);
      setCurrentPlan(planWithError);
    } finally {
      setIsGenerating(false);
    }
  };

  // Function to allow manual editing of a section
  const editSection = async (sectionId: SectionId, newContent: string): Promise<boolean> => {
    if (!currentPlan) return false;
    
    try {
      // Create a local copy of the plan
      const updatedPlan = { ...currentPlan };
      const updatedSections = { ...updatedPlan.sections };
      
      // Update the section content
      updatedSections[sectionId] = {
        ...updatedSections[sectionId],
        content: newContent,
        status: 'completed'
      };
      
      updatedPlan.sections = updatedSections;
      
      // Save the updated plan
      console.log(`Saving edited content for section ${sectionId}`);
      console.log(`New content length: ${newContent.length} characters`);
      
      // Save to Supabase
      const savedPlan = await saveBusinessPlan(updatedPlan);
      setCurrentPlan(savedPlan);
      
      // Update the list of saved plans
      setSavedPlans(prev => {
        const existingPlanIndex = prev.findIndex(p => p.id === savedPlan.id);
        if (existingPlanIndex >= 0) {
          const updatedPlans = [...prev];
          updatedPlans[existingPlanIndex] = savedPlan;
          return updatedPlans;
        } else {
          return [savedPlan, ...prev];
        }
      });
      
      toast.success(`Section ${sectionId} updated successfully`);
      return true;
    } catch (error) {
      console.error(`Error updating section ${sectionId}:`, error);
      toast.error(`Failed to update section ${sectionId}`);
      return false;
    }
  };

  // Handle submitting the business plan wizard
  const handleWizardSubmit = async (wizardData: BusinessPlanAdditionalData) => {
    if (!selectedIdea) {
      toast.error('Please select a business idea first');
      setShowWizard(false);
      return;
    }
    
    setError(null);
    setShowWizard(false); // Close the wizard after submission
    
    try {
      // Create a new business plan
      const newPlan: Partial<BusinessPlanType> = {
        id: '', // Will be assigned by the backend
        title: wizardData.businessName || `${selectedIdea.title} Business Plan`,
        idea_id: selectedIdea.id || '',
        sections: createEmptySections(),
        status: 'generating',
        content: {
          executive_summary: '',
          market_analysis: '',
          organization: '',
          financial_plan: '',
          marketing_strategy: ''
        },
        executive_summary: '',
        market_analysis: '',
        organization: '',
        financial_plan: '',
        marketing_strategy: '',
        businessIdea: selectedIdea,
        version: 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      // Save the initial plan to get an ID
      const savedPlan = await saveBusinessPlan(newPlan);
      setCurrentPlan(savedPlan);
      
      // Generate the business plan sections
      await generateBusinessPlan(savedPlan, wizardData);
      
    } catch (err) {
      console.error('Error creating business plan:', err);
      setError('Failed to create business plan');
      toast.error('Failed to create business plan');
    }
  };



  // Delete a plan
  const handleDeletePlan = async (planId: string) => {
    try {
      // Find the plan to get its idea_id
      const planToDelete = savedPlans.find(plan => plan.id === planId);
      if (!planToDelete) {
        console.error('Plan not found for deletion:', planId);
        return;
      }

      console.log('Deleting plan:', planToDelete);
      
      // Delete the plan from Supabase
      await deleteBusinessPlanFromSupabase(planId);
      
      // Update the UI immediately
      setSavedPlans(prev => prev.filter(plan => plan.id !== planId));
      
      // If the deleted plan was the current plan, clear it
      if (currentPlan && currentPlan.id === planId) {
        setCurrentPlan(null);
      }
      
      // Track deleted plans by idea_id in localStorage to prevent future fetching
      try {
        if (planToDelete.idea_id) {
          const deletedPlansJson = localStorage.getItem('deleted_business_plans');
          const deletedPlans = deletedPlansJson ? JSON.parse(deletedPlansJson) : [];
          if (!deletedPlans.includes(planToDelete.idea_id)) {
            deletedPlans.push(planToDelete.idea_id);
            localStorage.setItem('deleted_business_plans', JSON.stringify(deletedPlans));
            console.log(`Added idea ID: ${planToDelete.idea_id} to deleted plans in localStorage`);
          }
        }
      } catch (e) {
        console.error('Error updating deleted plans in localStorage:', e);
      }
      
      toast.success('Business plan deleted successfully');
    } catch (error) {
      console.error('Error deleting business plan:', error);
      setError('Failed to delete business plan');
    }
  };

  // Delete all plans for the current user
  const handleDeleteAllPlans = async () => {
    try {
      const result = await deleteAllBusinessPlansForCurrentUser();
      
      // Clear saved plans list
      setSavedPlans([]);
      setCurrentPlan(null);
      
      toast.success('All business plans deleted');
    } catch (error) {
      console.error('Error deleting all business plans:', error);
      setError('Failed to delete all business plans');
    }
  };

  // Fetch a specific business plan by ID
  const fetchBusinessPlan = async (ideaId: string, planId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get the business plan from Supabase
      const plan = await getBusinessPlanById(planId);
      
      if (plan) {
        setCurrentPlan(plan as BusinessPlanType);
      } else {
        setError('Business plan not found');
      }
    } catch (error) {
      console.error('Error fetching business plan:', error);
      setError('Failed to fetch business plan');
    } finally {
      setIsLoading(false);
    }
  };

  // Force cleanup of all plans in Supabase
  const handleForceCleanup = async () => {
    try {
      if (window.confirm('WARNING: This will permanently delete ALL business plans from the database. Are you sure?')) {
        const { count } = await deleteAllBusinessPlansForCurrentUser();
        toast.success(`Successfully deleted ${count} business plans from the database`);
        setSavedPlans([]);
        setCurrentPlan(null);
      }
    } catch (error) {
      console.error('Error during force cleanup:', error);
      toast.error('Failed to clean up business plans');
    }
  };

  return (
    <section className="business-plan-page py-12 relative overflow-hidden">
      {/* Background with gradient and mesh */}
      <div className="absolute inset-0 bg-gray-50 z-0">
        <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIiBmaWxsPSJub25lIj48cGF0aCBkPSJNLjUuNWg2MHY2MEguNVYuNXptMzAgMTVhMTUgMTUgMCAwIDAtMTUgMTUgMTUgMTUgMCAwIDAgMTUgMTUgMTUgMTUgMCAwIDAgMTUtMTUgMTUgMTUgMCAwIDAtMTUtMTV6bTAgMGExNSAxNSAwIDAgMC0xNSAxNSAxNSAxNSAwIDAgMCAxNSAxNSAxNSAxNSAwIDAgMCAxNS0xNSAxNSAxNSAwIDAgMC0xNS0xNXoiIHN0cm9rZT0iIzIwMjAyMCIgc3Ryb2tlLW9wYWNpdHk9Ii4wNiIvPjwvc3ZnPg==')] bg-[size:50px_50px]"></div>
      </div>
      
      {/* Main content */}
      <div className="container relative z-10 mx-auto px-4">
        {/* Decorative elements */}
        <div className="absolute top-20 left-12 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        <div className="absolute -bottom-32 right-12 w-64 h-64 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      
        {/* Section Title with 3D effect */}
        <div className="relative mb-10 text-center">
          <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 inline-block mb-2">
            Business Plan Generator
          </h1>
          <div className="h-1 w-48 mx-auto rounded-full bg-gradient-to-r from-blue-400 to-purple-500"></div>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">Create comprehensive, AI-powered business plans from your business ideas</p>
        </div>
      
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left sidebar */}
        <div className="lg:col-span-1">
          <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg transition-all duration-300 hover:shadow-xl">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 opacity-10"></div>
            <SavedIdeasList 
              ideas={savedIdeas} 
              onSelectIdea={handleIdeaSelect} 
              selectedIdea={selectedIdea} 
            />
          </div>
        </div>
        
        {/* Main content area */}
        <div className="lg:col-span-3">
          <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 to-blue-500 opacity-5"></div>
            <SavedPlansNavigation 
              savedPlans={savedPlans} 
              currentPlanId={currentPlan?.id || null} 
              onSelectPlan={handlePlanSelect} 
              onCreateNewPlan={handleCreateNewPlan} 
              onDeletePlan={handleDeletePlan}
              onDeleteAllPlans={handleDeleteAllPlans}
              onForceCleanup={handleForceCleanup}
              maxSavedPlans={MAX_SAVED_PLANS}
            />
          </div>
          {currentPlan && (
            <div className="flex space-x-2 mb-4">
              <button
                className="flex items-center px-5 py-2.5 bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg shadow hover:shadow-lg text-gray-700 mr-2 transition-all duration-300 transform hover:-translate-y-0.5"
                onClick={() => setShowPDF(!showPDF)}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {showPDF ? 'Hide PDF Preview' : 'Show PDF Preview'}
              </button>
              {/* Single PDF export button used throughout the application */}
              <BusinessPlanPDFExport plan={currentPlan} />
            </div>
          )}
          {isLoading ? (
            <div className="text-center py-12 relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 opacity-5"></div>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-700">Loading business plan...</p>
            </div>
          ) : error ? (
            <div className="relative overflow-hidden rounded-xl bg-red-100/70 backdrop-blur-sm border border-red-200 text-red-700 px-6 py-4 shadow relative" role="alert">
              <div className="absolute inset-0 bg-gradient-to-br from-red-400 to-red-300 opacity-5"></div>
              <strong className="font-bold">Error: </strong>
              <span className="block sm:inline">{error}</span>
            </div>
          ) : selectedIdea && !currentPlan ? (
            <div className="mt-6">
              <button
                className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-lg hover:shadow-blue-500/50 transition-all duration-300 transform hover:-translate-y-0.5 mr-2"
                onClick={() => setShowWizard(true)}
              >
                Create Business Plan
              </button>
            </div>
          ) : isGenerating ? (
            <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 opacity-5"></div>
              <GenerationProgress idea={selectedIdea} businessPlan={currentPlan || undefined} />
            </div>
          ) : currentPlan ? (
            <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 opacity-5"></div>
              <BusinessPlanDocument 
                plan={currentPlan}
                loading={isLoading}
                error={error}
                refreshPlan={() => {
                  if (selectedIdea?.id && currentPlan?.id) {
                    fetchBusinessPlan(selectedIdea.id, currentPlan.id);
                  }
                }}
                updateBusinessPlan={async (updatedPlan) => {
                  try {
                    const savedPlan = await saveBusinessPlan(updatedPlan);
                    setSavedPlans(prev => prev.map(plan => 
                      plan.id === updatedPlan.id ? {...plan, ...updatedPlan as BusinessPlanType} : plan
                    ));
                    return savedPlan;
                  } catch (error) {
                    console.error('Error saving business plan:', error);
                    return null;
                  }
                }}
                onEditSection={editSection}
              />
            </div>
          ) : (
            <div className="text-center py-12 relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 opacity-5"></div>
              <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">No business plan selected</h3>
              <p className="mt-1 text-gray-500">
                {savedPlans.length > 0 
                  ? 'Select a business plan from above or create a new one.'
                  : selectedIdea 
                    ? 'Click "Create Plan From Your Idea" to generate a business plan.'
                    : 'Select a business idea from the left sidebar or create a plan from your own idea.'}
              </p>
              {selectedIdea && (
                <div className="mt-6">
                  <button
                    onClick={() => setShowWizard(true)}
                    className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-lg hover:shadow-blue-500/50 transition-all duration-300 transform hover:-translate-y-0.5"
                  >
                    Create Plan for Selected Idea
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Business Plan Wizard */}
      {showWizard && selectedIdea && (
        <BusinessPlanWizard 
          businessIdea={selectedIdea} 
          onSubmit={handleWizardSubmit} 
          onCancel={() => setShowWizard(false)} 
        />
      )}
      
      {/* Business Plan PDF */}
      {currentPlan && showPDF && (
        <div className="mt-8 p-6 relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 opacity-5"></div>
          <h2 className="text-xl font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Business Plan PDF</h2>
          <div id="pdf-preview" className="border border-gray-200/50 p-5 rounded-lg bg-gray-50/80 backdrop-blur-sm">
            <p className="text-center text-gray-700">Here you can preview how your business plan will appear in the PDF. Use the "Export PDF" button at the top to download it.</p>
            {/* PDF content preview goes here */}
            <div className="mt-4 p-8 bg-white/90 backdrop-blur-sm border border-gray-100 rounded-lg shadow-sm">
              <h1 className="text-3xl font-bold text-center mb-6">{currentPlan.title || 'Business Plan'}</h1>
              <h3 className="text-xl text-center text-gray-600 mb-8">{currentPlan.businessIdea?.title || 'Business Idea'}</h3>
              
              {/* Sample Table of Contents */}
              <div className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">Table of Contents</h2>
                <div className="pl-4">
                  {Object.entries(currentPlan.sections || {}).filter(([id, section]) => 
                    id !== 'cover-page' && id !== 'table-of-contents' && 
                    section.content && section.content.trim() !== ''
                  ).map(([id, section], index) => (
                    <div key={id} className="flex items-center my-2">
                      <span>{index + 1}. {section.title}</span>
                      <span className="flex-grow mx-2 border-b border-dotted border-gray-400"></span>
                      <span>{index + 3}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Sample Section Preview */}
              {Object.entries(currentPlan.sections || {}).filter(([id, section]) => 
                id !== 'cover-page' && id !== 'table-of-contents' && 
                section.content && section.content.trim() !== ''
              ).slice(0, 1).map(([id, section], index) => (
                <div key={id} className="mb-6">
                  <div className="flex items-center mb-4 pb-2 border-b">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-500 text-white mr-3">{index + 1}</span>
                    <h2 className="text-2xl font-semibold">{section.title}</h2>
                  </div>
                  <div className="pl-4 prose prose-sm max-w-none">
                    <p className="text-gray-600">[Preview of section content...]</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  </section>
  );
};

export default BusinessPlan;
